import type { ReactNode } from "react";
import type { BlogStatus, SocialStatus } from "../lib/api";
import {
  BLOG_STAGES,
  SOCIAL_STAGES,
  blogStageIndex,
  socialStageIndex,
  blogTerminal,
  socialTerminal,
  type Stage,
  type TerminalState,
} from "./pipelineStages";
import { IconAlertCircle, IconAlertTriangle, IconCheck, IconX } from "./icons";

type Kind = "blog" | "social";
type Variant = "compact" | "full";

interface PipelineProgressProps {
  kind: Kind;
  status: BlogStatus | SocialStatus;
  reviewPassed?: boolean | null;
  errorMessage?: string | null;
  variant?: Variant;
  /** Extra content rendered below the active stage hint (e.g. "Fix flagged issues" button). */
  action?: ReactNode;
  className?: string;
}

function resolveStages(kind: Kind): Stage<string>[] {
  return (kind === "blog" ? BLOG_STAGES : SOCIAL_STAGES) as Stage<string>[];
}

function resolveIndex(kind: Kind, status: string): number {
  return kind === "blog"
    ? blogStageIndex(status as BlogStatus)
    : socialStageIndex(status as SocialStatus);
}

function resolveTerminal(kind: Kind, status: string): TerminalState {
  return kind === "blog"
    ? blogTerminal(status as BlogStatus)
    : socialTerminal(status as SocialStatus);
}

export default function PipelineProgress({
  kind,
  status,
  reviewPassed,
  errorMessage,
  variant = "full",
  action,
  className = "",
}: PipelineProgressProps) {
  const stages = resolveStages(kind);
  const terminal = resolveTerminal(kind, status);
  const activeIdx = resolveIndex(kind, status);

  if (variant === "compact") {
    return (
      <CompactProgress
        stages={stages}
        activeIdx={activeIdx}
        terminal={terminal}
        reviewPassed={reviewPassed}
        className={className}
      />
    );
  }

  return (
    <FullProgress
      stages={stages}
      activeIdx={activeIdx}
      terminal={terminal}
      reviewPassed={reviewPassed}
      errorMessage={errorMessage}
      action={action}
      className={className}
    />
  );
}

/* ---------- Compact variant (dashboard cards) ---------- */

function CompactProgress({
  stages,
  activeIdx,
  terminal,
  reviewPassed,
  className,
}: {
  stages: Stage<string>[];
  activeIdx: number;
  terminal: TerminalState;
  reviewPassed?: boolean | null;
  className: string;
}) {
  const total = stages.length;

  if (terminal === "failed") {
    return (
      <div className={className}>
        <div className="flex gap-0.5">
          {stages.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-sm bg-red-200 first:rounded-l-md last:rounded-r-md"
            />
          ))}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-700">
          <IconAlertCircle size={12} />
          Failed
        </div>
      </div>
    );
  }

  if (terminal === "rejected") {
    return (
      <div className={className}>
        <div className="flex gap-0.5">
          {stages.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-sm bg-ink-200 first:rounded-l-md last:rounded-r-md"
            />
          ))}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-ink-500">
          <IconX size={12} />
          Rejected
        </div>
      </div>
    );
  }

  const active = activeIdx >= 0 ? stages[activeIdx] : null;
  const needsFlagFix =
    active?.key === "awaiting_approval" && reviewPassed === false;

  return (
    <div className={className}>
      <div className="flex gap-0.5">
        {stages.map((_, i) => {
          const state: "done" | "active" | "upcoming" =
            i < activeIdx ? "done" : i === activeIdx ? "active" : "upcoming";
          const color =
            state === "done"
              ? "bg-brand-500"
              : state === "active"
                ? needsFlagFix
                  ? "bg-amber-400"
                  : "progress-stripes"
                : "bg-ink-100";
          return (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-sm ${color} first:rounded-l-md last:rounded-r-md`}
            />
          );
        })}
      </div>
      {active && (
        <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
          <span
            className={`font-medium truncate ${
              needsFlagFix ? "text-amber-700" : "text-ink-700"
            }`}
          >
            {needsFlagFix ? "Needs your attention" : active.label}
          </span>
          <span className="text-ink-400 tabular-nums shrink-0">
            {Math.min(activeIdx + 1, total)}/{total}
          </span>
        </div>
      )}
    </div>
  );
}

/* ---------- Full variant (blog detail hero) ---------- */

function FullProgress({
  stages,
  activeIdx,
  terminal,
  reviewPassed,
  errorMessage,
  action,
  className,
}: {
  stages: Stage<string>[];
  activeIdx: number;
  terminal: TerminalState;
  reviewPassed?: boolean | null;
  errorMessage?: string | null;
  action?: ReactNode;
  className: string;
}) {
  if (terminal === "failed") {
    return (
      <div
        className={`rounded-lg border border-red-200 bg-red-50/60 p-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-8 w-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
            <IconAlertCircle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-red-800">Pipeline failed</p>
            {errorMessage && (
              <p className="text-sm text-red-700 mt-0.5 whitespace-pre-wrap">
                {errorMessage}
              </p>
            )}
            {action && <div className="mt-3">{action}</div>}
          </div>
        </div>
      </div>
    );
  }

  if (terminal === "rejected") {
    return (
      <div
        className={`rounded-lg border border-ink-200 bg-ink-50 p-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-8 w-8 rounded-full bg-ink-100 text-ink-500 flex items-center justify-center">
            <IconX size={18} />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-ink-700">Rejected</p>
            <p className="text-sm text-ink-500 mt-0.5">
              This item was rejected and won't be published.
            </p>
            {action && <div className="mt-3">{action}</div>}
          </div>
        </div>
      </div>
    );
  }

  const active = activeIdx >= 0 ? stages[activeIdx] : null;
  const needsFlagFix =
    active?.key === "awaiting_approval" && reviewPassed === false;

  return (
    <div className={className}>
      {/* Stepper */}
      <div className="overflow-x-auto -mx-1 px-1 pb-1">
        <ol className="flex items-start min-w-max">
          {stages.map((s, i) => {
            const isDone = i < activeIdx;
            const isActive = i === activeIdx;
            const isAmber = isActive && needsFlagFix;
            const isLast = i === stages.length - 1;
            return (
              <li
                key={s.key}
                className="flex items-start first:pl-0 shrink-0"
                style={{ flex: isLast ? "0 0 auto" : "1 0 auto" }}
              >
                <div className="flex flex-col items-center shrink-0 w-[88px] md:w-[112px]">
                  <Dot
                    state={
                      isDone
                        ? "done"
                        : isActive
                          ? isAmber
                            ? "amber"
                            : "active"
                          : "upcoming"
                    }
                  />
                  <div className="mt-2 text-center px-1">
                    <div
                      className={`text-[11px] md:text-xs font-medium leading-tight ${
                        isDone
                          ? "text-ink-700"
                          : isActive
                            ? isAmber
                              ? "text-amber-700"
                              : "text-brand-700"
                            : "text-ink-400"
                      }`}
                    >
                      {s.label}
                    </div>
                  </div>
                </div>
                {!isLast && (
                  <div className="flex-1 h-[2px] mt-[13px] min-w-8">
                    <div
                      className={`h-full rounded-full ${
                        i < activeIdx ? "bg-brand-500" : "bg-ink-100"
                      }`}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Active hint + optional contextual action */}
      {active && (
        <div
          className={`mt-4 rounded-lg border px-4 py-3 ${
            needsFlagFix
              ? "border-amber-200 bg-amber-50/60"
              : "border-brand-100 bg-brand-50/50"
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center ${
                needsFlagFix
                  ? "bg-amber-100 text-amber-700"
                  : "bg-brand-100 text-brand-700"
              }`}
            >
              {needsFlagFix ? (
                <IconAlertTriangle size={14} />
              ) : active.key === "published" ? (
                <IconCheck size={14} />
              ) : (
                <span className="h-2.5 w-2.5 rounded-full bg-current pulse-dot" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold ${
                  needsFlagFix ? "text-amber-800" : "text-ink-800"
                }`}
              >
                {needsFlagFix ? "Review notes to address" : active.label}
              </p>
              <p
                className={`text-sm mt-0.5 ${
                  needsFlagFix ? "text-amber-700" : "text-ink-600"
                }`}
              >
                {needsFlagFix
                  ? "The review agent left some notes. Regenerate with them as feedback, or approve as-is."
                  : active.hint}
              </p>
              {action && <div className="mt-3">{action}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dot({
  state,
}: {
  state: "done" | "active" | "upcoming" | "amber";
}) {
  if (state === "done") {
    return (
      <div className="h-7 w-7 rounded-full bg-brand-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-brand-900/20">
        <IconCheck size={14} />
      </div>
    );
  }
  if (state === "active") {
    return (
      <div className="h-7 w-7 rounded-full bg-white border-2 border-brand-500 flex items-center justify-center shrink-0">
        <span className="h-2.5 w-2.5 rounded-full bg-brand-500 pulse-dot" />
      </div>
    );
  }
  if (state === "amber") {
    return (
      <div className="h-7 w-7 rounded-full bg-white border-2 border-amber-400 flex items-center justify-center shrink-0">
        <IconAlertTriangle size={13} className="text-amber-600" />
      </div>
    );
  }
  return (
    <div className="h-7 w-7 rounded-full bg-white border-2 border-ink-200 flex items-center justify-center shrink-0">
      <span className="h-1.5 w-1.5 rounded-full bg-ink-300" />
    </div>
  );
}
